import { useEffect, useState } from "react";
import { ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Field, TextInput, Toggle, SaveBar } from "@/admin/ui";

const SERVICES = [
  { key: "ga4", label: "Google Analytics 4", idKey: "ga4_id", idLabel: "GA4 Measurement ID", hint: "Format: G-XXXXXXXXXX", link: "https://analytics.google.com" },
  { key: "gtm", label: "Google Tag Manager", idKey: "gtm_id", idLabel: "GTM Container ID", hint: "Format: GTM-XXXXXXX", link: "https://tagmanager.google.com" },
  { key: "meta", label: "Meta Pixel", idKey: "meta_pixel_id", idLabel: "Pixel ID", hint: "Numeric ID from Meta Events Manager", link: "https://events.facebook.com" },
  { key: "capi", label: "Meta Conversions API", idKey: "capi_token", idLabel: "CAPI Access Token", hint: "Stored for server-side events — never exposed to visitors", link: "https://events.facebook.com" },
  { key: "gsc", label: "Google Search Console", idKey: "gsc_verification", idLabel: "Verification Meta Content", hint: "The content value of the google-site-verification tag", link: "https://search.google.com/search-console" },
  { key: "clarity", label: "Microsoft Clarity", idKey: "clarity_id", idLabel: "Clarity Project ID", hint: "From Clarity dashboard", link: "https://clarity.microsoft.com" },
  { key: "tiktok", label: "TikTok Pixel", idKey: "tiktok_pixel_id", idLabel: "Pixel ID", hint: "From TikTok Events Manager", link: "https://ads.tiktok.com" },
  { key: "linkedin", label: "LinkedIn Insight Tag", idKey: "linkedin_partner_id", idLabel: "Partner ID", hint: "Numeric ID from LinkedIn Campaign Manager", link: "https://www.linkedin.com/campaignmanager" },
];

export default function AnalyticsManager() {
  const { settings, reload } = useSite();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.analytics && !draft) setDraft(JSON.parse(JSON.stringify(settings.analytics)));
  }, [settings]);

  if (!draft) {
    return <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 text-accent-bright animate-spin" /></div>;
  }

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { analytics: draft });
      await reload();
      toast.success("Tracking settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-analytics-page">
      <h1 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight mb-2">Analytics & Tracking</h1>
      <p className="text-xs text-subtle mb-2">Paste IDs and toggle each tool on. Scripts load only when the visitor accepts cookies in the consent banner.</p>
      <p className="text-xs text-accent-bright mb-8 flex items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5" /> Cookie-consent governed — nothing fires without "Accept All".
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SERVICES.map((s, i) => (
          <div key={s.key} className="glass rounded-2xl p-5 sm:p-6" data-testid={`analytics-card-${s.key}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-sm font-semibold text-white">{s.label}</h3>
              <Toggle
                checked={!!draft[`${s.key}_enabled`]}
                onChange={(v) => setDraft((d) => ({ ...d, [`${s.key}_enabled`]: v }))}
                testid={`analytics-toggle-${s.key}`}
              />
            </div>
            <Field label={s.idLabel} hint={s.hint}>
              <TextInput value={draft[s.idKey]} onChange={(e) => setDraft((d) => ({ ...d, [s.idKey]: e.target.value }))} testid={`analytics-id-${s.key}`} />
            </Field>
            <a href={s.link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-subtle hover:text-accent-bright inline-flex items-center gap-1 transition-colors">
              Where do I find this? <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        ))}
      </div>

      <SaveBar onSave={save} saving={saving} testid="analytics-save-btn" />
    </div>
  );
}
